// Create a task group (S88), in the step-by-step style of Isomorphic's multi-step forms
// (shared/multi-step/multi-step-2): one rounded card per step with its title, "Step X of N" and
// Back/Next, beside a rail of the steps and what was chosen in each. The rules live in
// tasks/create/draft.ts; the API takes the same body the apps send.
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router";

import { accessQuery } from "../auth/access";
import { useT } from "../i18n";
import { detailCrumbs } from "../layout/nav";
import { toast } from "../layout/toast";
import { credentialsQuery } from "../secrets/api";
import { createTaskGroup } from "../tasks/create/api";
import { setToolchains } from "../tasks/toolchains/api";
import { type Credential, createBody, credentialRequirements, type Draft, reachable, STEPS, type StepKey } from "../tasks/create/draft";
import { Navigator } from "../tasks/create/frame/Navigator";
import { StepHeader } from "../tasks/create/frame/StepHeader";
import { StepRail } from "../tasks/create/frame/StepRail";
import { stepProblem } from "../tasks/create/problems";
import { AccessStep } from "../tasks/create/steps/AccessStep";
import { ReviewStep } from "../tasks/create/steps/ReviewStep";
import { SourceStep } from "../tasks/create/steps/SourceStep";
import { TypeStep } from "../tasks/create/steps/TypeStep";
import { WorkflowStep } from "../tasks/create/steps/WorkflowStep";
import { useAddSecret } from "../tasks/create/useAddSecret";
import { useWizard } from "../tasks/create/useWizard";
import { marketplaceTypesQuery } from "../tasks/lookups";
import { cardClass } from "../ui/Card";
import { cn } from "../ui/cn";
import { PageHeader } from "../ui/PageHeader";
import { ErrorState } from "../ui/states";

const EMPTY: Credential[] = [];

function useSummaries(d: Draft): Partial<Record<StepKey, string>> {
  const { tasks } = useT();
  const types = useQuery(marketplaceTypesQuery());
  const bindable = credentialRequirements(d.workflow);
  const projects = d.source === "jira" && !d.jira.raw ? d.jira.query.projects.join(", ") : "";
  return {
    type: types.data?.find((t) => t.id === d.taskTypeId)?.name,
    workflow: d.workflow?.name,
    source: [tasks.source[d.source], projects || (d.source === "github" ? d.github.repo : "")].filter(Boolean).join(" · "),
    access: bindable.length ? `${bindable.filter(({ index }) => d.bindings[index]).length} / ${bindable.length}` : undefined,
  };
}

export default function NewTaskGroupPage() {
  const { tasks, shell } = useT();
  const t = tasks.create;
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const orgId = useQuery(accessQuery()).data?.personal_org_id;
  const credentials = useQuery(credentialsQuery(orgId)).data ?? EMPTY;
  const { draft, step, change, go, finish } = useWizard(credentials);
  const adder = useAddSecret(orgId, (c, target) => (target === "source" ? change({ credentialId: c.id, bindings: {} }) : change({ bindings: { ...draft.bindings, [target]: c.id } })));
  const summaries = useSummaries(draft);
  const create = useMutation({
    mutationFn: async () => {
      const group = await createTaskGroup(createBody(draft));
      // Toolchains (D90) are the group's own resource: set once it exists.
      if (draft.toolchains.length > 0) {
        await setToolchains(group.id, draft.toolchains, null).catch((e: unknown) =>
          toast(t.toolchainsNotSet(e instanceof Error ? e.message : String(e)), "warn"),
        );
      }
      return group;
    },
    onSuccess: async (group) => {
      finish();
      toast(t.created(group.name));
      await queryClient.invalidateQueries({ queryKey: ["task-groups"] });
      void navigate(`/task-groups/${group.id}`);
    },
  });

  const key = STEPS[step]!;
  const last = step === STEPS.length - 1;
  const copy = t.steps[key];
  const body = {
    type: <TypeStep draft={draft} onChange={change} />,
    workflow: <WorkflowStep draft={draft} onChange={change} />,
    source: <SourceStep draft={draft} onChange={change} credentials={credentials} onAdd={adder.open && ((kind) => adder.open?.(kind, "source"))} />,
    access: <AccessStep draft={draft} onChange={change} credentials={credentials} onAdd={adder.open} />,
    review: <ReviewStep draft={draft} onChange={change} credentials={credentials} onEdit={go} />,
  }[key];

  return (
    <>
      <PageHeader title={shell.pages.newTaskGroup.title} breadcrumb={detailCrumbs("taskGroups", "/task-groups", { name: shell.pages.newTaskGroup.title })} description={shell.pages.newTaskGroup.subtitle} />
      <div className="grid items-start gap-6 lg:grid-cols-[15rem_minmax(0,1fr)] xl:gap-10">
        <StepRail label={t.progress} steps={STEPS.map((s) => ({ key: s, label: t.steps[s].label, summary: summaries[s] }))} current={step} reachable={reachable(draft)} onJump={go} />
        <section aria-label={copy.title} className={cn(cardClass, "min-w-0 max-w-4xl rounded-[20px] p-5 md:p-8 lg:p-10")}>
          <StepHeader title={copy.title} description={copy.description} step={step} total={STEPS.length} counter={t.stepOf(step + 1, STEPS.length)} />
          {body}
          {create.isError ? <ErrorState className="mt-6" title={t.createFailed} message={create.error.message} /> : null}
          <Navigator
            first={step === 0}
            last={last}
            blocked={stepProblem(draft, key)}
            busy={create.isPending}
            onBack={() => go(step - 1)}
            onNext={() => (last ? create.mutate() : go(step + 1))}
          />
        </section>
      </div>
      {adder.drawer}
    </>
  );
}
